/**
 * Clinical AI Agent - Voice Dictation Tab
 */

import React, { useState, useRef, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Mic, MicOff, Loader2, CheckCircle, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { VOICE_ASSESSMENT_TYPES, API_URL } from "./constants";

export default function VoiceTab({
  patientId,
  loading,
  setLoading,
  isDark,
  onAssessmentComplete,
  token
}) {
  const [isRecording, setIsRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [audioChunks, setAudioChunks] = useState([]);
  const [voiceAssessmentType, setVoiceAssessmentType] = useState("pain");
  const [voiceResult, setVoiceResult] = useState(null);
  const [autoSubmitVoice, setAutoSubmitVoice] = useState(false);

  // Start voice recording
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream, { mimeType: "audio/webm" });
      
      const chunks = [];
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunks.push(e.data);
        }
      };
      
      recorder.onstop = async () => {
        const audioBlob = new Blob(chunks, { type: "audio/webm" });
        await processVoiceRecording(audioBlob);
        stream.getTracks().forEach(track => track.stop());
      };
      
      setMediaRecorder(recorder);
      setAudioChunks(chunks);
      setIsRecording(true);
      recorder.start();
      
      toast.info("Recording started - speak your assessment");
    } catch (error) {
      toast.error("Could not access microphone");
    }
  };

  // Stop recording
  const stopRecording = () => {
    if (mediaRecorder && isRecording) {
      mediaRecorder.stop();
      setIsRecording(false);
    }
  };

  // Process voice recording
  const processVoiceRecording = async (audioBlob) => {
    try {
      setLoading(true);
      
      // Convert to base64
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64Audio = reader.result.split(",")[1];
        
        const res = await fetch(`${API_URL}/api/clinical-ai-agent/voice/dictate`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            audio_base64: base64Audio,
            assessment_type: voiceAssessmentType,
            patient_id: patientId,
            auto_submit: autoSubmitVoice
          })
        });
        
        if (res.ok) {
          const data = await res.json();
          setVoiceResult(data);
          
          if (data.auto_submitted && data.assessment_result) {
            toast.success("Voice assessment auto-submitted");
            onAssessmentComplete?.(data.assessment_result.type, data.assessment_result);
          } else {
            toast.success("Voice transcribed - review below");
          }
        } else {
          toast.error("Voice processing failed");
        }
      };
      reader.readAsDataURL(audioBlob);
      
    } catch (error) {
      toast.error("Error processing voice recording");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <Card className={isDark ? "bg-slate-800/50 border-slate-700" : "bg-white"}>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Mic className="h-4 w-4 text-cyan-400" />
            Voice Dictation
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label className="text-xs">Assessment Type</Label>
            <Select
              value={voiceAssessmentType}
              onValueChange={setVoiceAssessmentType}
            >
              <SelectTrigger className={isDark ? "bg-slate-700 border-slate-600" : ""}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {VOICE_ASSESSMENT_TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center justify-between">
            <Label className="text-xs">Auto-submit when ready</Label>
            <Switch
              checked={autoSubmitVoice}
              onCheckedChange={setAutoSubmitVoice}
            />
          </div>

          <div className="flex justify-center py-4">
            <Button
              size="lg"
              variant={isRecording ? "destructive" : "default"}
              className={isRecording 
                ? "h-20 w-20 rounded-full animate-pulse"
                : "h-20 w-20 rounded-full bg-gradient-to-r from-cyan-600 to-blue-600"
              }
              onClick={isRecording ? stopRecording : startRecording}
              disabled={loading}
              data-testid="voice-record-btn"
            >
              {loading ? (
                <Loader2 className="h-8 w-8 animate-spin" />
              ) : isRecording ? (
                <MicOff className="h-8 w-8" />
              ) : (
                <Mic className="h-8 w-8" />
              )}
            </Button>
          </div>

          <p className={`text-center text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>
            {isRecording 
              ? "Recording... Click to stop" 
              : loading 
              ? "Processing..." 
              : "Click to start voice dictation"}
          </p>
        </CardContent>
      </Card>

      {/* Voice Result */}
      {voiceResult && (
        <Card className={`${isDark ? "bg-slate-800/80 border-cyan-500/30" : "bg-cyan-50 border-cyan-200"}`}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-400" />
              Transcription Result
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ScrollArea className="max-h-64">
              <div className="space-y-3">
                <div>
                  <p className="text-xs font-semibold mb-1">Transcript:</p>
                  <p className={`text-sm p-2 rounded ${isDark ? "bg-slate-700" : "bg-white"}`}>
                    {voiceResult.transcript}
                  </p>
                </div>

                <div className="flex items-center gap-2">
                  <Badge variant="outline">{voiceResult.assessment_type}</Badge>
                  <Badge className={
                    voiceResult.suggested_action === "ready_to_submit" 
                      ? "bg-green-500/20 text-green-400"
                      : voiceResult.suggested_action === "needs_clarification"
                      ? "bg-yellow-500/20 text-yellow-400"
                      : "bg-gray-500/20 text-gray-400"
                  }>
                    {voiceResult.suggested_action?.replace(/_/g, " ")}
                  </Badge>
                  {voiceResult.confidence_score && (
                    <Badge variant="outline">
                      {voiceResult.confidence_score}% confidence
                    </Badge>
                  )}
                </div>

                {voiceResult.extracted_data && Object.keys(voiceResult.extracted_data).length > 0 && (
                  <div>
                    <p className="text-xs font-semibold mb-1">Extracted Data:</p>
                    <div className={`p-2 rounded text-xs ${isDark ? "bg-slate-700" : "bg-white"}`}>
                      {Object.entries(voiceResult.extracted_data).map(([key, value]) => (
                        <div key={key} className="flex gap-2">
                          <span className="font-medium">{key}:</span>
                          <span>{typeof value === "object" ? JSON.stringify(value) : String(value)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {voiceResult.clarification_needed && voiceResult.clarification_needed.length > 0 && (
                  <div className="p-2 rounded bg-yellow-500/10 border border-yellow-500/20">
                    <p className="text-xs font-semibold mb-1 text-yellow-400">Clarification Needed:</p>
                    <ul className="text-xs space-y-1">
                      {voiceResult.clarification_needed.map((q, i) => (
                        <li key={i} className="flex items-start gap-1">
                          <AlertCircle className="h-3 w-3 text-yellow-400 mt-0.5" />
                          {q}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {voiceResult.auto_submitted && (
                  <Badge className="bg-green-500/20 text-green-400">
                    Assessment Auto-Submitted
                  </Badge>
                )}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
